/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  composeReducers,
  composeSetters,
  fieldValidationSetter,
  setDirty,
  setEntityField,
  setEntityFields,
  setNestedEntityField,
  setRecalculationNeeded,
  setUpdatedDirtyType,
} from '@gh/common-shared';
import { arrayIterator } from '@gh/core-util';
import { constant, first } from 'lodash';
import {
  getQuoteHousePlanTopId, QuoteHousePlanTop,
  quoteHousePlanTopTable,
  validateQuoteHousePlanTopField,
} from '../../resources/quote.model';
import {
  queryQuoteTopsInvalid,
  QuoteTopDialogState,
  QuoteTopDialogStateEvent,
  QuoteTopDialogStateEventType,
} from './quote-top-dialog.state';

export const setTopField = composeSetters(
  setEntityField,
  setUpdatedDirtyType,
  fieldValidationSetter(validateQuoteHousePlanTopField));

export const quoteTopDialogReducer = composeReducers(mainReducer, globalFlagsReducer);

function mainReducer(state: QuoteTopDialogState,
                     event: QuoteTopDialogStateEvent): QuoteTopDialogState {
  switch (event.type) {
    case QuoteTopDialogStateEventType.Load: {
      const { priceBookId, tops, formMode } = event;
      const { takeOffParams, costCenters } = tops;
      return { ...state,
        priceBookId, costCenters, tops: quoteHousePlanTopTable.create(arrayIterator(takeOffParams)), formMode };
    }
    case QuoteTopDialogStateEventType.Merge: {
      const { takeOffParams, costCenters } = event.tops;
      return {
        ...state,
        costCenters,
        isRecalculationNeeded: false,
        tops: quoteHousePlanTopTable.mergeAll(
          state.tops,
          takeOffParams,
          (oldTop: QuoteHousePlanTop, newTop: QuoteHousePlanTop) => setEntityField(oldTop, 'value', newTop.value)),
      };
    }
    case QuoteTopDialogStateEventType.SetTopValue:
      return setEntityField(
        state,
        'tops',
        quoteHousePlanTopTable.mergeById(state.tops, event.topId, (top) => setTopField(top, 'value', event.value)));
    case QuoteTopDialogStateEventType.CommitTop:
      return setEntityField(
        state,
        'tops',
        quoteHousePlanTopTable.mergeById(
          state.tops,
          event.topId,
          (top) => top.isFromGto ? setEntityField(top, 'isFromGto', false) : top));
    case QuoteTopDialogStateEventType.ResetTop:
      return setEntityField(
        state,
        'tops',
        quoteHousePlanTopTable.mergeById(state.tops, event.top.topId, constant(event.top)));
    case QuoteTopDialogStateEventType.ResetFocusedCell:
      return setNestedEntityField(state, ['selection', 'focusedCell'], void 0);
    case QuoteTopDialogStateEventType.NavigateToInvalid: {
      const invalidTop = first(queryQuoteTopsInvalid(state));
      return setNestedEntityField(
        state,
        ['selection', 'focusedCell'],
        invalidTop ? { id: getQuoteHousePlanTopId(invalidTop), field: 'value' } : void 0);
    }
    default:
      return state;
  }
}

function globalFlagsReducer(state: QuoteTopDialogState, event: QuoteTopDialogStateEvent): QuoteTopDialogState {
  switch (event.type) {
    case QuoteTopDialogStateEventType.CommitTop:
      return setDirty(setRecalculationNeeded(state));
    default:
      return state;
  }
}
